import Account from "../../../../Account";
import filters from "../../../../filters";
import { createDeepEqualSelector } from "../../entities/utils/createDeepEqualSelector";

import type { RootState } from "../../entities";

export const selectSwiftWireTemplateDetails = createDeepEqualSelector(
  [
    (state: RootState) => state.swiftwireTemplates.byId,
    (state: RootState) => state.recipients.byId,
    (state: RootState) => state.accounts.byId,
    (_, swiftWireTemplateId?: API.SwiftWireTemplateId) => swiftWireTemplateId,
  ],
  (templatesById, recipientsById, accountsById, swiftWireTemplateId) => {
    if (!swiftWireTemplateId) {
      return null;
    }

    const template = templatesById[swiftWireTemplateId];
    if (!template) {
      return null;
    }
    const recipient = recipientsById[template.recipient];
    if (!recipient) {
      return null;
    }
    const account = accountsById[template.from_account];
    if (!account) {
      return null;
    }

    try {
      return {
        template,
        recipient,
        account: Account.deserialize(account),
      };
    } catch (e) {
      return null;
    }
  },
);

const sortByLastUsed = (a: API.SwiftWireTemplate, b: API.SwiftWireTemplate) => {
  if (!a.last_used && !b.last_used) return 0;
  if (!a.last_used) return 1;
  if (!b.last_used) return -1;
  return Date.parse(b.last_used) - Date.parse(a.last_used);
};

export const selectFilteredSwiftWireTemplateIds = createDeepEqualSelector(
  [
    (state: RootState) => state.swiftwireTemplates.byId,
    (state: RootState) => state.recipients.byId,
    (state: RootState) => state.accounts.byId,
    (_, currency?: string) => currency,
    (_, _currency?: string, searchQuery?: string) => searchQuery,
  ],
  (
    templatesById: Record<API.SwiftWireTemplateId, API.SwiftWireTemplate>,
    recipientsById: Record<API.RecipientId, API.Recipient>,
    accountsById: Record<API.AccountId, API.AnyAccount>,
    currency: string | undefined,
    searchQuery: string | undefined,
  ) => {
    const templates = Object.values(templatesById);

    // Filter by currency
    const currencyFiltered = templates.filter(
      (t) => !t.recipient_currency || t.recipient_currency === currency,
    );

    // Filter by search query
    const query = searchQuery?.trim().toLowerCase();
    if (!query) {
      return currencyFiltered.sort(sortByLastUsed).map((t) => t.id);
    }

    return currencyFiltered
      .filter((t) => {
        // Template name
        if (t.name.toLowerCase().includes(query)) return true;

        // Recipient name
        const recipient = recipientsById[t.recipient];
        if (recipient?.name?.toLowerCase().includes(query)) return true;

        // Account name (nickname || name)
        const rawAccount = accountsById[t.from_account];
        if (rawAccount) {
          try {
            const accountName = Account.deserialize(rawAccount)
              .getName()
              .toLowerCase();
            if (accountName.includes(query)) return true;
          } catch {
            if (rawAccount.name.toLowerCase().includes(query)) return true;
            if (rawAccount.nickname?.toLowerCase().includes(query)) return true;
          }
        }

        // Formatted amount (e.g. "$1,234.56")
        if (t.amount) {
          const formatted = filters.currency(t.amount as Dollars, {
            hasDecimal: true,
          });
          if (formatted.toLowerCase().includes(query)) return true;
        }

        return false;
      })
      .sort(sortByLastUsed)
      .map((t) => t.id);
  },
);
